import { ID, RelationshipUpdate, Task, UpdateTask } from '@griply/types';

import { GoalTaskCountDelta } from './build-task-link-writes';
import { buildTaskUpdateWithLinks } from './build-task-update-with-links';

export type TaskLinkUpdate = {
  task: Task;
  update: UpdateTask;
};

export type TaskLinkBatchWrites = {
  taskUpdates: UpdateTask[];
  relationshipUpdates: RelationshipUpdate[];
  goalCountDeltas: GoalTaskCountDelta[];
};

// One count write per goal: the rules reject a 2nd write to a document within 100ms.
const foldCountDeltas = (
  deltas: GoalTaskCountDelta[],
): GoalTaskCountDelta[] => {
  const byGoalId = new Map<ID, GoalTaskCountDelta>();

  deltas.forEach(({ goalId, taskCount, completedTaskCount }) => {
    const current = byGoalId.get(goalId) ?? {
      goalId,
      taskCount: 0,
      completedTaskCount: 0,
    };
    byGoalId.set(goalId, {
      goalId,
      taskCount: current.taskCount + taskCount,
      completedTaskCount: current.completedTaskCount + completedTaskCount,
    });
  });

  return [...byGoalId.values()].filter(
    ({ taskCount, completedTaskCount }) => taskCount || completedTaskCount,
  );
};

export const buildTaskUpdatesWithLinks = (
  updates: TaskLinkUpdate[],
): TaskLinkBatchWrites => {
  const writes = updates.map((update) => buildTaskUpdateWithLinks(update));

  return {
    taskUpdates: writes.flatMap(({ taskUpdate }) =>
      taskUpdate ? [taskUpdate] : [],
    ),
    relationshipUpdates: writes.flatMap(
      ({ relationshipUpdates }) => relationshipUpdates,
    ),
    goalCountDeltas: foldCountDeltas(
      writes.flatMap(({ goalCountDeltas }) => goalCountDeltas),
    ),
  };
};
